import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useGetGymsQuery, useGetSessionsQuery } from "../../../services";
import {
  useAssignSessionScheduleMutation,
  useGetSessionSchedulesByGymQuery,
  useGetSessionSchedulesQuery,
  useDeleteSessionScheduleMutation,
} from "../../../services";
import { toast } from "../../../core/toast";

const weekDays = ["S", "M", "T", "W", "T", "F", "S"];
function getInitialCalendar(view: string) {
  if (view === "week") {
    return Array(7)
      .fill([])
      .map(() => []);
  } else {
    // 6 rows x 7 cols
    return Array(42)
      .fill([])
      .map(() => []);
  }
}

// Build a 42-cell grid including previous/next month dates
function getMonthGrid(year: number, month: number) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const grid: { day: number; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const dayNum = i - startDay + 1;
    if (dayNum <= 0) grid.push({ day: prevMonthDays + dayNum, inMonth: false });
    else if (dayNum > daysInMonth)
      grid.push({ day: dayNum - daysInMonth, inMonth: false });
    else grid.push({ day: dayNum, inMonth: true });
  }
  return grid;
}

// ADD: helpers to handle dates in local timezone (avoid UTC ISO shift)
const toLocalYMD = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const parseDateFromApi = (value: any): Date => {
  if (!value) return new Date(NaN);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d); // local date (no timezone shift)
  }
  return new Date(value);
};

// WEEK VIEW CELL - theme-aware
// Types for session items and calendar cells
type SessionItem = {
  id: string;
  scheduleId?: string;
  code: string;
  type: string;
};

type CalendarCellWeekProps = {
  items: SessionItem[];
  onDrop: (item: SessionItem, cellIdx: number) => void;
  cellIdx: number;
  getDateForCell: (cellIdx: number) => Date;
  onRemove?: (session: SessionItem, cellIdx: number) => void;
};

const CalendarCellWeek: React.FC<CalendarCellWeekProps> = ({
  items,
  onDrop,
  cellIdx,
  getDateForCell,
  onRemove,
}) => {
  const { theme } = useTheme();
  const cellBg = theme === "dark" ? "#1a1a1a" : "#ffffff";
  const normalBorder = theme === "dark" ? "#817d7dff" : "#9CA3AF";
  const normalText = theme === "dark" ? "#9CA3AF" : "#4B5563";
  const chipBg = theme === "dark" ? "#2a2a2a" : "#F3F4F6";

  // Date check removed

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "SIDEBAR_ITEM",
    drop: (item) => onDrop(item as SessionItem, cellIdx),
    canDrop: (item) => {
      return true;
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  const dropZoneStyle = {
    minHeight: "120px",
    height: "auto",
    background: cellBg,
    opacity: 1,
    cursor: "auto",
  };

  if (isOver && !canDrop) {
    dropZoneStyle.background = "#ef4444"; // Red background for invalid drop
  } else if (isOver && canDrop) {
    dropZoneStyle.background = "rgba(182, 203, 36, 0.2)"; // Green background for valid drop
  }

  return (
    <div ref={drop} className="flex flex-col gap-1.5 p-2" style={dropZoneStyle}>
      {items.map((session: SessionItem, sidx: number) => {
        const isPremium = session.type === "Premium";
        const borderColor = isPremium ? "#B6CB24" : normalBorder;
        const textColor = isPremium ? "#B6CB24" : normalText;

        return (
          <div
            key={sidx}
            className="flex items-center justify-between gap-1.5 px-2.5 py-1 rounded-full font-medium text-xs"
            style={{
              backgroundColor: chipBg,
              border: `1px solid ${borderColor}`,
              color: textColor,
            }}
          >
            <span className="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                <circle
                  cx="9"
                  cy="9"
                  r="7"
                  stroke={borderColor}
                  strokeWidth="1.5"
                  fill={isPremium ? "rgba(182, 203, 36, 0.15)" : "transparent"}
                />
              </svg>
              <span className="font-semibold">{session.code}</span>
            </span>
            <button
              type="button"
              aria-label="Remove schedule"
              className={`h-5 w-5 flex items-center justify-center rounded-full border ${theme === "dark"
                ? "border-gray-500 text-gray-300"
                : "border-gray-400 text-gray-600"
                } hover:bg-red-500 hover:text-white hover:border-red-500 transition`}
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.(session, cellIdx);
              }}
            >
              ×
            </button>
          </div>
        );
      })}

    </div>
  );
};

// MONTH VIEW CELL - theme-aware
// Types for month cell props
type CalendarCellMonthProps = {
  items: SessionItem[];
  onDrop: (item: SessionItem, cellIdx: number) => void;
  cellIdx: number;
  date: number;
  inMonth: boolean;
  isToday: boolean;
  getDateForCell: (cellIdx: number) => Date;
  onRemove?: (session: SessionItem, cellIdx: number) => void; // Added onRemove
};
const CalendarCellMonth: React.FC<CalendarCellMonthProps> = ({
  items,
  onDrop,
  cellIdx,
  date,
  inMonth,
  isToday,
  getDateForCell,
  onRemove, // Added onRemove
}) => {
  const { theme } = useTheme();
  const cellBg = theme === "dark" ? "#1a1a1a" : "#ffffff";
  const inMonthColor = theme === "dark" ? "#D1D5DB" : "#374151";
  const outMonthColor = theme === "dark" ? "#6B7280" : "#9CA3AF";
  const chipBg = theme === "dark" ? "#2a2a2a" : "#F3F4F6";
  const normalBorder = theme === "dark" ? "#817d7dff" : "#9CA3AF";
  const normalText = theme === "dark" ? "#9CA3AF" : "#4B5563";

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "SIDEBAR_ITEM",
    drop: (item) => onDrop(item as SessionItem, cellIdx),
    canDrop: (item) => true,
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  const dropZoneStyle = {
    minHeight: "100px", // Increased slightly to give room to drop
    height: "100%",
    background: cellBg,
    opacity: 1,
  };

  if (isOver && !canDrop) {
    dropZoneStyle.background = "#ef4444"; // Red background for invalid drop
  } else if (isOver && canDrop) {
    dropZoneStyle.background = "rgba(182, 203, 36, 0.2)"; // Green background for valid drop
  }

  return (
    <div
      ref={drop}
      className="flex flex-col gap-1 p-2 relative"
      style={dropZoneStyle}
    >
      <div
        className="absolute right-2 top-1 text-xs font-medium"
        style={{ color: inMonth ? inMonthColor : outMonthColor }}
      >
        {date}
      </div>

      {/* Render the individual dragged sessions here */}
      <div className="mt-4 flex flex-col gap-1 overflow-y-auto max-h-[80px] scrollbar-hide">
        {items.map((session: SessionItem, sidx: number) => {
          const isPremium = session.type === "Premium";
          const borderColor = isPremium ? "#B6CB24" : normalBorder;
          const textColor = isPremium ? "#B6CB24" : normalText;

          return (
            <div
              key={sidx}
              className="flex items-center justify-between gap-1 px-1.5 py-0.5 rounded font-medium text-[10px]"
              style={{
                backgroundColor: chipBg,
                border: `1px solid ${borderColor}`,
                color: textColor,
              }}
            >
              <span className="truncate">{session.code}</span>
              <button
                type="button"
                aria-label="Remove schedule"
                className="hover:text-red-500 transition ml-1 font-bold text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove?.(session, cellIdx);
                }}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Sidebar item - theme-aware
// Sidebar item props
type SidebarItemProps = {
  item: SessionItem;
};

const SidebarItem: React.FC<SidebarItemProps> = ({ item }) => {
  const { theme } = useTheme();
  const [{ isDragging }, drag] = useDrag({
    type: "SIDEBAR_ITEM",
    item,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const boxCls =
    theme === "dark"
      ? "bg-[#232323] text-gray-100 border-[#232323]"
      : "bg-white text-gray-900 border-gray-300";

  return (
    <div
      ref={drag}
      className={`flex items-center justify-between px-3 py-2 rounded-lg mb-2 border cursor-move ${boxCls}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="flex items-center gap-2 relative">
        <span className="font-semibold text-sm">{item.code}</span>
      </div>
      <span
        className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${item.type === "Premium"
          ? "border-[#B6CB24] text-[#B6CB24]"
          : theme === "dark"
            ? "border-gray-400 text-gray-400"
            : "border-gray-300 text-gray-600"
          } ${theme === "dark" ? "bg-[#232323]" : "bg-white"}`}
      >
        {item.type}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <circle
            cx="6"
            cy="6"
            r="5"
            stroke={
              item.type === "Premium"
                ? "#B6CB24"
                : theme === "dark"
                  ? "#A0A0A0"
                  : "#9CA3AF"
            }
            strokeWidth="2"
          />
        </svg>
      </span>
    </div>
  );
};

const Schedule: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [selectedGymId, setSelectedGymId] = useState<string>("");

  const {
    data: gymsData,
    isLoading: isGymsLoading,
    isError: isGymsError,
  } = useGetGymsQuery();

  const {
    data: sessionsData,
    isLoading: isSessionsLoading,
    isError: isSessionsError,
  } = useGetSessionsQuery();

  // Fetch all session schedules (for initial load)
  const {
    data: allSessionSchedulesData,
    isLoading: isAllSessionSchedulesLoading,
    isError: isAllSessionSchedulesError,
  } = useGetSessionSchedulesQuery();

  // Fetch session schedules by selected gym
  const {
    data: gymSessionSchedulesData,
    isLoading: isGymSessionSchedulesLoading,
    isError: isGymSessionSchedulesError,
  } = useGetSessionSchedulesByGymQuery(selectedGymId, {
    skip: !selectedGymId,
  });

  // Add create schedule mutation
  const [assignSessionSchedule] = useAssignSessionScheduleMutation();
  const [deleteSessionSchedule] = useDeleteSessionScheduleMutation();

  const gymsList = React.useMemo(() => {
    const src: any = gymsData;
    if (!src) return [];
    if (Array.isArray(src)) return src;
    if (Array.isArray(src?.data)) return src.data;
    if (Array.isArray(src?.items)) return src.items;
    if (Array.isArray(src?.results)) return src.results;
    return [];
  }, [gymsData]);
  const sessionsList = React.useMemo(() => {
    const src: any = sessionsData;
    if (!src) return [];
    if (Array.isArray(src)) return src;
    if (Array.isArray(src?.data)) return src.data;
    if (Array.isArray(src?.items)) return src.items;
    if (Array.isArray(src?.results)) return src.results;
    return [];
  }, [sessionsData]);
  const sessionSchedulesList = React.useMemo(() => {
    const src: any = selectedGymId
      ? gymSessionSchedulesData
      : allSessionSchedulesData;
    if (!src) return [];
    if (Array.isArray(src)) return src;
    if (Array.isArray(src?.data)) return src.data;
    if (Array.isArray(src?.items)) return src.items;
    if (Array.isArray(src?.results)) return src.results;
    return [];
  }, [selectedGymId, gymSessionSchedulesData, allSessionSchedulesData]);

  // Get current loading and error states
  const isSessionSchedulesLoading = selectedGymId
    ? isGymSessionSchedulesLoading
    : isAllSessionSchedulesLoading;
  const isSessionSchedulesError = selectedGymId
    ? isGymSessionSchedulesError
    : isAllSessionSchedulesError;

  const getGymId = (g: any) =>
    g?.id ?? g?._id ?? g?.gymId ?? g?.uuid ?? `${g?.value ?? ""}`;
  const getGymLabel = (g: any) =>
    g?.name ?? g?.gymName ?? g?.title ?? g?.label ?? `Gym ${getGymId(g)}`;

  const [calendarView, setCalendarView] = useState<"week" | "month">("week");
  const [calendarData, setCalendarData] = useState<SessionItem[][]>(
    getInitialCalendar("week")
  );

  // State for current month/year
  const [currentDate, setCurrentDate] = useState(new Date());

  // Keep a 42-cell grid for the current month
  const [monthGrid, setMonthGrid] = useState(
    getMonthGrid(currentDate.getFullYear(), currentDate.getMonth())
  );

  // Recompute grid and reset calendar data when month/view changes
  useEffect(() => {
    if (calendarView === "month") {
      setMonthGrid(
        getMonthGrid(currentDate.getFullYear(), currentDate.getMonth())
      );
      setCalendarData(getInitialCalendar("month"));
    } else {
      setCalendarData(getInitialCalendar("week"));
    }
  }, [currentDate, calendarView]);

  // Helper for month name
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthLabel = `${monthNames[currentDate.getMonth()]
    } ${currentDate.getFullYear()}`;

  // Handlers for prev/next month
  const handlePrevMonth = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  };
  const handleNextMonth = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  };
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  // Helper function to get date for dropped cell
  const getDateForCell = (cellIdx: number) => {
    if (calendarView === "week") {
      // Use the week that contains currentDate
      const anchor = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate()
      );
      anchor.setHours(0, 0, 0, 0);
      const startOfWeek = new Date(anchor);
      startOfWeek.setDate(anchor.getDate() - anchor.getDay()); // Sunday start
      const cellDate = new Date(startOfWeek);
      cellDate.setDate(startOfWeek.getDate() + cellIdx);
      return cellDate;
    } else {
      // Month view
      const cellData = monthGrid[cellIdx];
      if (!cellData) return new Date();

      let targetDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        cellData.day
      );

      // If it's not in current month, adjust the date
      if (!cellData.inMonth) {
        if (cellData.day > 15) {
          // Previous month
          targetDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() - 1,
            cellData.day
          );
        } else {
          // Next month
          targetDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            cellData.day
          );
        }
      }

      return targetDate;
    }
  };

  // Handle drop from sidebar to calendar cell
  const handleDrop = async (item: SessionItem, cellIdx: number) => {
    // Check if gym is selected
    if (!selectedGymId) {
      toast.error("Please select a gym first before scheduling sessions.");
      return;
    }

    const scheduleDate = getDateForCell(cellIdx);
    // Past date check removed

    // Use local date (no UTC) for comparisons and payload
    const dateString = toLocalYMD(scheduleDate);

    // Check if this specific session already exists for this gym on this date
    const duplicateSession = sessionSchedulesList.find((schedule: any) => {
      const scheduleGymId =
        schedule.gym?.id || schedule.gym?._id || schedule.gymId || schedule.gym;
      const scheduleSessionId =
        schedule.session?.id ||
        schedule.session?._id ||
        schedule.sessionId ||
        schedule.session;

      // Parse API date safely (preserve local date if it's YYYY-MM-DD)
      const schedDateStr = toLocalYMD(
        parseDateFromApi(
          schedule.date || schedule.scheduledDate || schedule.schedule_date
        )
      );

      return (
        String(scheduleGymId) === String(selectedGymId) &&
        String(scheduleSessionId) === String(item.id) &&
        schedDateStr === dateString
      );
    });

    if (duplicateSession) {
      toast.error(
        `Session "${item.code
        }" is already scheduled for this gym on ${scheduleDate.toLocaleDateString()}.`
      );
      return;
    }

    try {
      // Send assignments array to match API
      const scheduleData = {
        session: item.id,
        gym: selectedGymId,
        date: dateString,
      };
      await assignSessionSchedule(scheduleData).unwrap();

      // Optimistic local update; API invalidation will sync
      setCalendarData((prev) =>
        prev.map((cell, idx) => (idx === cellIdx ? [...cell, item] : cell))
      );
    } catch (error) {
      console.error("Failed to create schedule:", error);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState<{
    cellIdx: number;
    sessionItem: any;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Replace previous delete logic with modal trigger
  const handleRemove = async (sessionItem: any, cellIdx: number) => {
    setDeleteTarget({ cellIdx, sessionItem });
  };

  const closeDelete = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { cellIdx, sessionItem } = deleteTarget;
    setIsDeleting(true);
    try {
      // original delete logic moved here
      let scheduleId = sessionItem.scheduleId;
      if (!scheduleId) {
        const cellDate = getDateForCell(cellIdx);
        const dateString = toLocalYMD(cellDate);
        const match = sessionSchedulesList.find((s: any) => {
          const sid =
            s.session?._id || s.sessionId || s.session?.id || s.session;
          const gid = s.gym?._id || s.gym?.id || s.gymId || s.gym;
          const d = toLocalYMD(
            parseDateFromApi(s.date || s.scheduledDate || s.schedule_date)
          );
          return (
            String(sid) === String(sessionItem.id) &&
            String(gid) === String(selectedGymId || s.gym) &&
            d === dateString
          );
        });
        scheduleId = match?._id || match?.id;
      }

      if (!scheduleId) {
        toast.error("Cannot find schedule to delete");
        setIsDeleting(false);
        return;
      }

      await deleteSessionSchedule(scheduleId).unwrap();

      setCalendarData((prev) =>
        prev.map((cell, idx) =>
          idx === cellIdx ? cell.filter((it: any) => it !== sessionItem) : cell
        )
      );
      setDeleteTarget(null);
    } catch {
      // errors handled upstream
    } finally {
      setIsDeleting(false);
    }
  };

  // Load scheduled sessions into calendar when data changes
  useEffect(() => {
    if (sessionSchedulesList.length > 0) {
      const newCalendarData: SessionItem[][] = getInitialCalendar(calendarView);

      sessionSchedulesList.forEach((schedule: any) => {
        const scheduleDate = parseDateFromApi(
          schedule.date || schedule.scheduledDate || schedule.schedule_date
        );
        const cellIdx = getCellIndexForDate(scheduleDate);
        if (cellIdx !== -1) {
          const sessionItem: SessionItem = {
            // keep session id to detect duplicates
            id:
              schedule.session?._id ||
              schedule.sessionId ||
              schedule.session?.id ||
              schedule.session,
            scheduleId: schedule._id || schedule.id, // for delete
            code:
              schedule.session?.sessionCode ||
              schedule.session?.code ||
              schedule.session?.name ||
              schedule.sessionCode ||
              schedule.code ||
              `Session ${schedule.id}`,
            type:
              schedule.session?.type ||
              schedule.session?.sessionType ||
              schedule.type ||
              schedule.sessionType ||
              "Normal",
          };
          newCalendarData[cellIdx] = [...newCalendarData[cellIdx], sessionItem];
        }
      });

      setCalendarData(newCalendarData);
    } else {
      setCalendarData(getInitialCalendar(calendarView));
    }
  }, [sessionSchedulesList, calendarView, currentDate]);

  // Helper function to get cell index for a date
  const getCellIndexForDate = (date: Date): number => {
    if (calendarView === "week") {
      // Map within the week that contains currentDate
      const anchor = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate()
      );
      anchor.setHours(0, 0, 0, 0);
      const startOfWeek = new Date(anchor);
      startOfWeek.setDate(anchor.getDate() - anchor.getDay()); // Sunday start
      const daysDiff = Math.floor(
        (date.getTime() - startOfWeek.getTime()) / (1000 * 60 * 60 * 24)
      );
      return daysDiff >= 0 && daysDiff < 7 ? daysDiff : -1;
    } else {
      // Month view
      for (let i = 0; i < monthGrid.length; i++) {
        const cellData = monthGrid[i];
        let cellDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          cellData.day
        );

        // Adjust for previous/next month cells
        if (!cellData.inMonth) {
          if (cellData.day > 15) {
            cellDate = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth() - 1,
              cellData.day
            );
          } else {
            cellDate = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth() + 1,
              cellData.day
            );
          }
        }

        if (cellDate.toDateString() === date.toDateString()) {
          return i;
        }
      }
      return -1;
    }
  };

  // Handle gym selection change
  const handleGymChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const gymId = e.target.value;
    setSelectedGymId(gymId);
  };

  // Add: filter change handler (fixes undefined error)
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextView: "week" | "month" =
      e.target.value === "Filter by Month" ? "month" : "week";
    setCalendarView(nextView);
    setCalendarData(getInitialCalendar(nextView));
    // monthGrid will recompute via useEffect when calendarView changes
  };

  // Render calendar grid (use theme-aware borders and header bg)
  const renderCalendar = () => {
    // theme-aware border color for grids
    const borderColor = theme === "dark" ? "#2a2a2a" : "#e5e7eb";
    const headerBg = theme === "dark" ? "#232323" : "#F3F4F1";
    const headerTextCls = theme === "dark" ? "text-gray-300" : "text-gray-700";

    if (calendarView === "week") {
      // Build the week that contains currentDate
      const anchor = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate()
      );
      anchor.setHours(0, 0, 0, 0);
      const startOfWeek = new Date(anchor);
      startOfWeek.setDate(anchor.getDate() - anchor.getDay()); // Sunday start

      const weekDates = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        return date;
      });

      return (
        <div
          className="grid  grid-cols-7 gap-0 rounded-lg overflow-hidden"
          style={{ border: `1px solid ${borderColor}` }}
        >
          {/* Header row with dates */}
          <div className="col-span-7 grid grid-cols-7">
            {weekDays.map((day, idx) => {
              const date = weekDates[idx];
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <div
                  key={day}
                  className={`text-center font-semibold py-2 text-sm ${headerTextCls}`}
                  style={{
                    backgroundColor: headerBg, // no color highlight for today
                    borderRight: idx < 6 ? `1px solid ${borderColor}` : "none",
                    borderBottom: `1px solid ${borderColor}`,
                    ...(isToday
                      ? {
                        // outline current weekday in header (top and sides)
                        borderLeft: `2px solid #B6CB24`,
                        borderRight: `2px solid #B6CB24`,
                        borderTop: `2px solid #B6CB24`,
                      }
                      : {}),
                  }}
                >
                  <div>{day}</div>
                  {/* removed date number */}
                  {/* <div className="text-xs font-normal">{date.getDate()}</div> */}
                </div>
              );
            })}
          </div>
          {/* Calendar cells */}
          {weekDays.map((day, idx) => {
            const date = weekDates[idx];
            const isToday = date.toDateString() === new Date().toDateString();
            return (
              <div
                key={day}
                className="flex flex-col"
                style={{
                  borderRight: idx < 6 ? `1px solid ${borderColor}` : "none",
                  ...(isToday
                    ? {
                      // outline current weekday column (bottom and sides)
                      borderLeft: `2px solid #B6CB24`,
                      borderRight: `2px solid #B6CB24`,
                      borderBottom: `2px solid #B6CB24`,
                    }
                    : {}),
                }}
              >
                <CalendarCellWeek
                  items={calendarData[idx] || []}
                  onDrop={handleDrop}
                  cellIdx={idx}
                  getDateForCell={getDateForCell}
                  onRemove={handleRemove}
                />
              </div>
            );
          })}
        </div>
      );
    } else {
      const today = new Date();
      return (
        <div
          className="rounded-lg overflow-hidden"
          style={{ border: `1px solid ${borderColor}` }}
        >
          {/* Header row */}
          <div className="grid grid-cols-7">
            {weekDays.map((day, idx) => (
              <div
                key={day}
                className={`text-center font-semibold py-2 text-sm ${headerTextCls}`}
                style={{
                  backgroundColor: headerBg,
                  borderRight: idx < 6 ? `1px solid ${borderColor}` : "none",
                  borderBottom: `1px solid ${borderColor}`,
                }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Month cells */}
          <div className="grid grid-cols-7">
            {monthGrid.map((d, idx) => {
              const isToday =
                d.inMonth &&
                today.getDate() === d.day &&
                today.getMonth() === currentDate.getMonth() &&
                today.getFullYear() === currentDate.getFullYear();

              return (
                <div
                  key={idx}
                  className="flex flex-col"
                  style={{
                    borderRight:
                      (idx + 1) % 7 !== 0 ? `1px solid ${borderColor}` : "none",
                    borderBottom:
                      idx < 35 ? `1px solid ${borderColor}` : "none",
                    ...(isToday ? { border: "2px solid #B6CB24" } : {}),
                  }}
                >
                  <CalendarCellMonth
                    items={calendarData[idx] || []}
                    onDrop={handleDrop}
                    cellIdx={idx}
                    date={d.day}
                    inMonth={d.inMonth}
                    isToday={isToday}
                    getDateForCell={getDateForCell}
                    onRemove={handleRemove}
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        className={`pt-20 pb-8 min-h-screen ${theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
          }`}
        style={{
          marginLeft: sidebarWidth,
          width: `calc(100% - ${sidebarWidth}px)`,
        }}
      >
        {/* Header */}
        <div className="px-4 md:px-6 lg:px-8 mb-2">
          <h1
            className={`text-xl font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-900"
              }`}
          >
            Schedule Overview
          </h1>
          <div className="text-sm mt-1 flex items-center gap-2">
            <span className="text-gray-400">Dashboard</span>
            <span className="text-gray-600">&gt;</span>
            <span className="text-[#B6CB24] font-medium">All Schedules</span>
          </div>
        </div>

        {/* Main Layout */}
        <div className="flex gap-4 px-4 md:px-6 lg:px-8">
          {/* Left Sidebar */}
          <div
            className={`w-[310px] min-w-[290px] rounded-xl shadow-lg border p-4 flex flex-col ${theme === "dark"
              ? "bg-[#181818] border-[#232323]"
              : "bg-white border-gray-200"
              }`}
            style={{ height: "480px" }}
          >
            <div className="flex items-center justify-between mb-4">
              <span
                className={`font-medium text-sm ${theme === "dark" ? "text-gray-100" : "text-gray-900"
                  }`}
              >
                Choose from List
              </span>
              <button
                className="flex items-center gap-2 px-3 py-1 rounded-full border border-[#B6CB24] font-medium text-[#B6CB24] bg-[#232323] hover:bg-[#232323]/90 transition"
                style={{
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.12) 0%, rgba(83,91,16,0.12) 100%)",
                }}
                onClick={() => navigate(adminRoutes.sessionCreate)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 2.00012C17.5228 2.00012 22 6.47727 22 12.0001C22 17.523 17.5228 22.0001 12 22.0001C6.47715 22.0001 2 17.523 2 12.0001M8.909 2.48699C7.9 2.8146 6.96135 3.29828 6.12153 3.90953M3.90943 6.12162C3.29806 6.9616 2.81432 7.90044 2.4867 8.90964"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 8.00012V16.0001M16 12.0001H8"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="font-medium text-xs">Create Session</span>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-[#232323] scrollbar-track-[#181818]">
              {isSessionsLoading ? (
                <div
                  className={`text-center py-4 ${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Loading sessions...
                </div>
              ) : isSessionsError ? (
                <div
                  className={`text-center py-4 ${theme === "dark" ? "text-red-400" : "text-red-600"
                    }`}
                >
                  Failed to load sessions
                </div>
              ) : sessionsList.length === 0 ? (
                <div
                  className={`text-center py-4 ${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  No sessions available
                </div>
              ) : (
                sessionsList.map((session: any, idx: number) => {
                  // Normalize session data
                  const sessionItem = {
                    id:
                      session?._id ||
                      session?.sessionId ||
                      session?.id ||
                      `session-${idx}`,
                    code:
                      session?.sessionCode ||
                      session?.code ||
                      session?.name ||
                      `S${idx + 1}`,
                    type: session?.type || session?.sessionType || "Normal",
                  };
                  return (
                    <SidebarItem key={sessionItem.id} item={sessionItem} />
                  );
                })
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col gap-4">
            {/* Calendar Card */}
            <div
              className={`flex-1 flex flex-col gap-4 rounded-lg border p-3 ${theme === "dark"
                ? "bg-[#181818] border-[#232323]"
                : "bg-white border-gray-200"
                }`}
            >
              {/* Top controls */}
              <div className="flex items-center justify-between mb-3">
                {/* Left: Assign to Gym Dropdown */}
                <div className="flex flex-col">
                  <select
                    className={`${theme === "dark"
                      ? "rounded-md px-4 py-2 border bg-[#232323] border-[#232323] text-gray-100"
                      : "rounded-md px-4 py-2 border bg-white border-gray-300 text-gray-900"
                      }`}
                    value={selectedGymId}
                    onChange={handleGymChange}
                    disabled={isGymsLoading || isGymsError}
                  >
                    <option value="" disabled>
                      {isGymsLoading
                        ? "Loading gyms..."
                        : isGymsError
                          ? "Failed to load gyms"
                          : "Choose from list"}
                    </option>
                    {!isGymsLoading &&
                      !isGymsError &&
                      gymsList.map((g: any) => {
                        const id = getGymId(g);
                        const label = getGymLabel(g);
                        return (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        );
                      })}
                  </select>
                  {selectedGymId && (
                    <div className="text-xs mt-1 text-gray-500">
                      {isSessionSchedulesLoading
                        ? "Loading scheduled sessions..."
                        : `${sessionSchedulesList.length} sessions scheduled for this gym`}
                    </div>
                  )}
                  {!selectedGymId && (
                    <div className="text-xs mt-1 text-gray-500">
                      {isSessionSchedulesLoading
                        ? "Loading all scheduled sessions..."
                        : `${sessionSchedulesList.length} total sessions scheduled`}
                    </div>
                  )}
                </div>

                {/* Center: Month/Year with arrows */}
                <div className="flex items-center gap-2">
                  {/* <button
                    className="p-1"
                    onClick={handlePrevMonth}
                    aria-label="Previous Month"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                    >
                      <path
                        d="M15 6C15 6 9.00001 10.4189 9 12C8.99999 13.5812 15 18 15 18"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button> */}
                  <span className="font-semibold text-lg text-[#B6CB24]">
                    {monthLabel}
                  </span>
                  {/* <button
                    className="p-1"
                    onClick={handleNextMonth}
                    aria-label="Next Month"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                    >
                      <path
                        d="M9 6C9 6 15 10.4189 15 12C15 13.5812 9 18 9 18"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button> */}
                </div>

                {/* Right: Today Button and Filter Select */}
                <div className="flex items-center gap-3 hidden">
                  <button
                    className="flex items-center gap-2 px-4 py-1 rounded-lg border border-[#B6CB24] text-black font-medium"
                    style={{
                      background:
                        "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                    }}
                    onClick={handleToday}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28249 21.5 7.52166 21.5 12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.5217 21.5 5.2825 20.1088 3.8912C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.8912C2.5 5.2825 2.5 7.5217 2.5 12Z"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="6" cy="8" r="1" fill="white" />
                      <circle cx="6" cy="12" r="1" fill="white" />
                      <circle cx="6" cy="16" r="1" fill="white" />
                      <circle cx="10" cy="8" r="1" fill="#C6D92C" />
                      <circle cx="10" cy="12" r="1" fill="white" />
                      <circle cx="10" cy="16" r="1" fill="white" />
                      <circle cx="14" cy="8" r="1" fill="white" />
                      <circle cx="14" cy="12" r="1" fill="white" />
                      <circle cx="14" cy="16" r="1" fill="white" />
                      <circle cx="18" cy="8" r="1" fill="white" />
                      <circle cx="18" cy="12" r="1" fill="white" />
                      <circle cx="18" cy="16" r="1" fill="white" />
                    </svg>
                    Today
                  </button>
                  <select
                    className={`${theme === "dark"
                      ? "bg-[#232323] border-[#232323] text-gray-100"
                      : "bg-white border-gray-300 text-gray-900"
                      } rounded-md p-1 border`}
                    value={
                      calendarView === "month"
                        ? "Filter by Month"
                        : "Filter by Week"
                    }
                    onChange={handleFilterChange}
                  >
                    <option>Filter by Week</option>
                    <option>Filter by Month</option>
                  </select>
                </div>
              </div>
              {renderCalendar()}
            </div>
          </div>
        </div>
      </div>
      {deleteTarget && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme === "dark" ? "bg-black/60" : "bg-black/40"
            }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${theme === "dark"
              ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
              : "bg-white text-gray-900 border-gray-200"
              }`}
          >
            <button
              onClick={closeDelete}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
                }`}
              disabled={isDeleting}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>

            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                  }`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#B6CB24">
                  <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">
                Delete Scheduled Session
              </h3>
              <p
                className={`${theme === "dark" ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
              >
                Are you sure you want to delete session "
                {deleteTarget.sessionItem?.code}"?
              </p>

              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid #D92C2C",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Confirm and delete"}
                </button>
                <button
                  type="button"
                  onClick={closeDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-black rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  May be later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DndProvider>
  );
};

export default Schedule;
